/* global window */

import { createAnalytics, defineEvents, SHARED_EVENTS, createIdentityJwtTokenGetter } from '@3plearning/mx-teacher-analytics';

import { getParam, getLocale } from './utils';
import { PRODUCT_IDS } from './enums';

const APP_NAME = 'teacher-class-management';

export const ANALYTICS_EVENTS = {
    pageView: 'page.view',
    sectionError: 'section.error',
    classRenamed: 'class.renamed',
    groupCreated: 'group.created',
    groupEdited: 'group.edited',
    groupRenamed: 'group.renamed',
    groupDeleted: 'group.deleted',
    multiYearGroupsCreated: 'groups.multi-year-created',
    passwordChanged: 'password.changed',
    studentsMoved: 'students.moved',
    studentsCopied: 'students.copied',
    studentsRemoved: 'students.removed',
    studentsGroupChanged: 'students.group-changed',
    studentsExported: 'students.exported',
    signInCardsDownloaded: 'signin.cards-downloaded'
};

const appEvents = defineEvents({
    ...SHARED_EVENTS,
    [ANALYTICS_EVENTS.classRenamed]: { group: 'classes', props: {} },
    [ANALYTICS_EVENTS.groupCreated]: { group: 'groups', props: {} },
    [ANALYTICS_EVENTS.groupEdited]: { group: 'groups', props: {} },
    [ANALYTICS_EVENTS.groupRenamed]: { group: 'groups', props: {} },
    [ANALYTICS_EVENTS.groupDeleted]: { group: 'groups', props: {} },
    [ANALYTICS_EVENTS.multiYearGroupsCreated]: { group: 'groups', props: {} },
    [ANALYTICS_EVENTS.passwordChanged]: { group: 'passwords', props: {} },
    [ANALYTICS_EVENTS.studentsMoved]: { group: 'students', props: {} },
    [ANALYTICS_EVENTS.studentsCopied]: { group: 'students', props: {} },
    [ANALYTICS_EVENTS.studentsRemoved]: { group: 'students', props: {} },
    [ANALYTICS_EVENTS.studentsGroupChanged]: { group: 'students', props: {} },
    [ANALYTICS_EVENTS.studentsExported]: { group: 'students', props: {} },
    [ANALYTICS_EVENTS.signInCardsDownloaded]: { group: 'signin', props: {} }
});

const TEACHER_TOTTER_PRODUCTS = {
    [PRODUCT_IDS.Mathletics]: 'mathletics',
    [PRODUCT_IDS.Mathletica]: 'mathletics',
    [PRODUCT_IDS.Spellodrome]: 'spellodrome',
    [PRODUCT_IDS.Phonics]: 'phonics',
    [PRODUCT_IDS.Readiwriter]: 'readiwriter'
};

function readEnv(key) {
    // eslint-disable-next-line no-underscore-dangle
    const config = window._env_;
    return config ? config[key] : undefined;
}

function getTeacherTotterProduct() {
    const productId = parseInt(getParam('productId'), 10) || PRODUCT_IDS.Mathletics;
    return TEACHER_TOTTER_PRODUCTS[productId] || 'mathletics';
}

function getIdentity() {
    return {
        locale: getLocale(),
        countryCode: getParam('countryCode') || '',
        userProfileId: getParam('userProfileId') || '',
        userProfileType: 'teacher',
        marketRegionId: getParam('regionId') || ''
    };
}

let client = null;

export function getAnalytics() {
    if (client) {
        return client;
    }

    const environment = readEnv('env') || getParam('env') || 'live';
    const teacherTotterSyncUrl = readEnv('AnalyticsTeacherTotterSyncUrl') || '';
    const teacherTotterEnabled = readEnv('TeacherTotterEnabled') === 'true'
        && getParam('isOneRosterActivatedSchool') === 'true';

    client = createAnalytics({
        app: APP_NAME,
        environment,
        providers: {
            posthog: { enabled: readEnv('PostHogEnabled') === 'true' },
            teacherTotter: {
                enabled: teacherTotterEnabled,
                syncUrl: teacherTotterSyncUrl,
                product: getTeacherTotterProduct(),
                getAuthToken: createIdentityJwtTokenGetter({
                    identityGatewayUrl: readEnv('AnalyticsIdentityGatewayUrl') || '',
                    getAppAuthToken: () => getParam('authToken') || null
                })
            }
        },
        enabledFeatureGroups: null,
        events: appEvents
    });

    client.setCommonProps(getIdentity());
    client.identify();

    return client;
}

// Analytics must never interrupt a teacher's flow, so failures are swallowed here.
export function trackEvent(name, props = {}) {
    try {
        const result = getAnalytics().track(name, props);
        if (result && typeof result.catch === 'function') {
            result.catch(() => {});
        }
    } catch (err) {
        // no-op
    }
}

export function trackSession(task) {
    try {
        getAnalytics().trackSession(task);
    } catch (err) {
        // no-op
    }
}



// WEBPACK FOOTER //
// ./helpers/analytics.js